import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { fileUrl } from "../../lib/api";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { fmtDate, fmtDateTime, timeAgo } from "../../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

const STATUSES = ["Submitted", "Under Review", "Additional Information Needed", "Approved", "Not Covered", "Contractor Assigned", "Scheduling", "Scheduled", "In Progress", "Work Completed", "Homeowner Confirmation", "Closed"];

function Panel({ title, children }) {
  return <div className="bg-arq-card border border-border rounded-md p-5"><p className="font-display text-lg mb-4">{title}</p>{children}</div>;
}

export default function AdminServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: sr, isLoading } = useQuery({ queryKey: ["admin-sr", id], queryFn: async () => (await api.get(`/service-requests/${id}`)).data });
  const { data: contractors = [] } = useQuery({ queryKey: ["contractors"], queryFn: async () => (await api.get("/contractors")).data });
  const { data: team = [] } = useQuery({ queryKey: ["users", "team"], queryFn: async () => (await api.get("/users?role=team")).data });

  const [note, setNote] = useState("");
  const [noteVisible, setNoteVisible] = useState(true);
  const [appt, setAppt] = useState({ date: "", start_time: "", duration: "1 hour", technician: "", notes: "" });
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-sr", id] }); qc.invalidateQueries({ queryKey: ["admin-service"] }); qc.invalidateQueries({ queryKey: ["admin-metrics"] }); };

  const patch = async (body) => { await api.patch(`/service-requests/${id}`, body); refresh(); toast.success("Updated"); };

  const addNote = async () => {
    if (!note) return;
    setBusy(true);
    try { await api.post(`/service-requests/${id}/activity`, { note, visible_to_homeowner: noteVisible }); setNote(""); refresh(); toast.success("Note added"); }
    finally { setBusy(false); }
  };

  const schedule = async () => {
    if (!appt.date) { toast.error("Pick a date"); return; }
    await api.post(`/service-requests/${id}/appointment`, appt); refresh(); toast.success("Appointment scheduled");
  };

  if (isLoading || !sr) return <Loader />;

  return (
    <div className="max-w-5xl space-y-6 arq-fade-up">
      <button onClick={() => navigate("/admin/service")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-arq-ink" data-testid="admin-back-service"><ArrowLeft className="h-4 w-4" />Back</button>

      <div className="bg-arq-card border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-sm text-arq-accent">Request #{sr.number}</p>
            <h1 className="font-display text-3xl text-arq-ink mt-1">{sr.category} · {sr.location}</h1>
            <p className="text-sm text-muted-foreground mt-1">{sr.homeowner_name} · {sr.property?.address}</p>
          </div>
          <StatusPill status={sr.status} />
        </div>
        <p className="text-sm text-muted-foreground mt-4 leading-relaxed whitespace-pre-line">{sr.description}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border text-sm">
          <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Priority</p><StatusPill status={sr.priority} /></div>
          <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Submitted</p><p>{fmtDate(sr.created_at)}</p></div>
          <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Discovered</p><p>{fmtDate(sr.date_discovered)}</p></div>
          <div><p className="text-[10px] uppercase tracking-widest text-muted-foreground">Entry Permission</p><p>{sr.permission_to_enter ? "Granted" : "No"}</p></div>
        </div>
        {sr.attachments && sr.attachments.length > 0 && (
          <div className="grid grid-cols-6 gap-2 mt-4">{sr.attachments.map((a, i) => a.content_type?.startsWith("image") && <img key={i} src={fileUrl(a.url)} alt="" className="aspect-square object-cover rounded-md" />)}</div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Manage Request">
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">Status</Label>
              <Select value={sr.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Warranty Determination</Label>
              <Input defaultValue={sr.warranty_determination} onBlur={(e) => e.target.value !== sr.warranty_determination && patch({ warranty_determination: e.target.value })} placeholder="e.g. Covered under builder warranty" data-testid="admin-warranty-det" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block">Assigned Team</Label>
                <Select value={sr.assigned_team_id || ""} onValueChange={(v) => patch({ assigned_team_id: v })}>
                  <SelectTrigger data-testid="admin-team-select"><SelectValue placeholder="Assign…" /></SelectTrigger>
                  <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block">Contractor</Label>
                <Select value={sr.assigned_contractor_id || ""} onValueChange={(v) => patch({ assigned_contractor_id: v, status: sr.status === "Approved" ? "Contractor Assigned" : sr.status })}>
                  <SelectTrigger data-testid="admin-contractor-select"><SelectValue placeholder="Assign…" /></SelectTrigger>
                  <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="mb-1.5 block">Resolution Notes</Label>
              <Textarea defaultValue={sr.resolution} onBlur={(e) => e.target.value !== sr.resolution && patch({ resolution: e.target.value })} rows={2} data-testid="admin-resolution" />
            </div>
          </div>
        </Panel>

        <Panel title="Schedule Appointment">
          {sr.appointment && (
            <div className="mb-4 p-3 bg-arq-surface/60 rounded-md text-sm">
              <p className="font-medium text-arq-ink">{fmtDate(sr.appointment.date)} at {sr.appointment.start_time}</p>
              <p className="text-xs text-muted-foreground">{sr.appointment.technician} · {sr.appointment.duration}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1.5 block">Date</Label><Input type="date" value={appt.date} onChange={(e) => setAppt({ ...appt, date: e.target.value })} data-testid="appt-date" /></div>
            <div><Label className="mb-1.5 block">Start Time</Label><Input value={appt.start_time} onChange={(e) => setAppt({ ...appt, start_time: e.target.value })} placeholder="9:00 AM" data-testid="appt-time" /></div>
            <div><Label className="mb-1.5 block">Duration</Label><Input value={appt.duration} onChange={(e) => setAppt({ ...appt, duration: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Technician</Label><Input value={appt.technician} onChange={(e) => setAppt({ ...appt, technician: e.target.value })} /></div>
          </div>
          <Button className="mt-4 w-full" onClick={schedule} data-testid="schedule-appt">Schedule Appointment</Button>
        </Panel>
      </div>

      <Panel title="Add Activity Note">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" data-testid="admin-note" />
        <div className="flex items-center justify-between mt-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={noteVisible} onCheckedChange={(v) => setNoteVisible(!!v)} data-testid="note-visible" />Visible to homeowner</label>
          <Button onClick={addNote} disabled={busy} data-testid="add-note-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Add Note</Button>
        </div>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Activity History">
          {(!sr.activities || sr.activities.length === 0) ? <p className="text-sm text-muted-foreground">No activity.</p> : (
            <div className="space-y-4">{sr.activities.map((a) => (
              <div key={a.id} className="border-l-2 border-arq-surface pl-3">
                <p className="text-sm text-arq-ink">{a.note}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{a.by} · {timeAgo(a.at)} · {a.visible_to_homeowner ? "Homeowner-visible" : "Internal"}</p>
              </div>
            ))}</div>
          )}
        </Panel>
        <Panel title="Status History">
          <div className="space-y-3">{sr.status_history.map((h, i) => (
            <div key={i} className="flex items-center justify-between gap-2"><StatusPill status={h.status} /><span className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.by}</span></div>
          ))}</div>
        </Panel>
      </div>
    </div>
  );
}
